import React from 'react';
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import PlaceholderGraph from 'rsuite/cjs/Placeholder/PlaceholderGraph';
import {t} from '@lingui/core/macro';

import {ServerMetricsChartRow} from 'features/server/lib/serverMetricsHelper';
import {hasServerMetricsValues} from 'features/server/lib/serverMetricsView';

import {PALETTE} from 'shared/config/model/palette';

import styles from './ServerMetricsChart.module.scss';

export interface IServerMetricsChartSeries {
    key: string,
    label: string,
    color: string,
}

interface IServerMetricsChartProps {
    title: string,
    data: ServerMetricsChartRow[],
    series: IServerMetricsChartSeries[],
    isLoading: boolean,
    // eslint-disable-next-line no-unused-vars
    tickFormatter: (ts: number) => string,
    // eslint-disable-next-line no-unused-vars
    tooltipLabelFormatter: (ts: number) => string,
    // eslint-disable-next-line no-unused-vars
    valueFormatter: (value: number, row?: ServerMetricsChartRow, key?: string) => string,
    yDomain?: [number, number | 'auto'],
    height?: number,
}

const ServerMetricsChart: React.FC<IServerMetricsChartProps> = ({
    title,
    data,
    series,
    isLoading,
    tickFormatter,
    tooltipLabelFormatter,
    valueFormatter,
    yDomain,
    height = 220,
}) => {
    const hasValues = hasServerMetricsValues(data, series.map((s) => s.key));

    const renderContent = () => {
        if (isLoading) {
            return <PlaceholderGraph active height={height} />;
        }

        if (!hasValues) {
            return (
                <div className={styles.empty} style={{height}}>
                    {t`Нет данных за выбранный период`}
                </div>
            );
        }

        return (
            <div className={styles.chartWrapper} style={{height}}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{top: 10, right: 8, left: 0, bottom: 0}}>
                        <CartesianGrid strokeDasharray="1 0" vertical={false} stroke={PALETTE.borderLight} />
                        <XAxis
                            dataKey="ts"
                            tickFormatter={tickFormatter}
                            tick={{fontSize: 12}}
                            minTickGap={24}
                        />
                        <YAxis
                            orientation="right"
                            width={40}
                            tick={{fontSize: 12}}
                            domain={yDomain ?? ['auto', 'auto']}
                            allowDecimals={false}
                        />
                        <Tooltip
                            labelFormatter={(ts: number) => tooltipLabelFormatter(ts)}
                            formatter={(
                                value: number,
                                name: string,
                                item: {dataKey?: string | number, payload?: ServerMetricsChartRow},
                            ) => valueFormatter(value, item?.payload, String(item?.dataKey ?? ''))}
                        />
                        {series.length > 1 && <Legend iconType="plainline" wrapperStyle={{fontSize: 12}} />}
                        {series.map((s) => (
                            <Line
                                key={s.key}
                                type="monotone"
                                name={s.label}
                                dataKey={s.key}
                                stroke={s.color}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{r: 4}}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>{title}</h3>
            {renderContent()}
        </div>
    );
};

export default ServerMetricsChart;
